import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, CheckCircle, Spinner, XCircle, ArrowRight } from "@phosphor-icons/react";

export default function PricingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", attempts: 0, info: null });
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", info: "Missing session id" }); return; }
    let attempts = 0;
    const poll = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/pro/checkout/status/${sessionId}`);
        if (!data.found) { setState({ status: "error", info: "Session not found" }); return; }
        if (data.payment_status === "paid") {
          if (refreshUser) await refreshUser();
          setState({ status: "paid", info: data });
          return;
        }
        if (data.applied) {
          if (refreshUser) await refreshUser();
          setState({ status: "paid", info: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", info: data });
          return;
        }
        if (attempts >= 10) {
          setState({ status: "timeout", info: data });
          return;
        }
        setState({ status: "polling", attempts, info: data });
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 5) {
          setState({ status: "error", info: e.response?.data?.detail || e.message });
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { stopRef.current = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      <div className="card-default p-8 md:p-10 text-center" data-testid="pricing-success-card">
        {state.status === "polling" && (
          <>
            <Spinner size={40} weight="bold" className="text-[#0F2537] mx-auto animate-spin" />
            <h1 className="font-heading font-black text-2xl mt-4">Confirming your payment…</h1>
            <p className="text-[#0F2537]/70 mt-2 text-sm">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#1E5631] text-white flex items-center justify-center">
              <Crown weight="fill" size={32} />
            </div>
            <h1 className="font-heading font-black text-3xl mt-4">You're Pro! 🏆</h1>
            <p className="text-[#0F2537]/70 mt-2">Welcome to Bula Pro. Unlock club averages, premium tournaments, and ad-free shareables.</p>
            <Link to="/dashboard" data-testid="pro-back-to-dashboard"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E05D3A] text-white font-bold hover:brightness-110">
              Go to dashboard <ArrowRight weight="bold" size={14} />
            </Link>
          </>
        )}
        {state.status === "expired" && (
          <>
            <XCircle size={40} weight="fill" className="text-red-500 mx-auto" />
            <h1 className="font-heading font-black text-2xl mt-4">Session expired</h1>
            <p className="text-[#0F2537]/70 mt-2">No charge was made. Try again?</p>
            <Link to="/pricing" className="mt-5 inline-block px-4 py-2 rounded-xl bg-[#0F2537] text-white font-bold">Back to pricing</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <CheckCircle size={40} weight="fill" className="text-[#0F2537] mx-auto" />
            <h1 className="font-heading font-black text-2xl mt-4">Still processing…</h1>
            <p className="text-[#0F2537]/70 mt-2">Refresh this page in a minute or check your Profile.</p>
            <Link to="/profile" className="mt-5 inline-block px-4 py-2 rounded-xl bg-[#0F2537] text-white font-bold">Open profile</Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={40} weight="fill" className="text-red-500 mx-auto" />
            <h1 className="font-heading font-black text-2xl mt-4">Something went wrong</h1>
            <p className="text-[#0F2537]/70 mt-2 text-sm">{String(state.info)}</p>
            <Link to="/pricing" className="mt-5 inline-block px-4 py-2 rounded-xl bg-[#0F2537] text-white font-bold">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
